import { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

interface SlowOperationBannerProps {
  /**
   * Whether the operation is still active (queued or processing).
   * The banner is only shown when `active` is true AND the threshold has elapsed.
   */
  active: boolean;
  /**
   * Wall-clock seconds after which the banner appears. Defaults to 90.
   */
  thresholdSeconds?: number;
  /**
   * Optional URL for a "Report an issue" link. If omitted the link is not shown.
   */
  reportUrl?: string;
  className?: string;
}

/**
 * SlowOperationBanner — appears after `thresholdSeconds` of continuous
 * `active` time with no completion, to reassure users rather than leaving
 * them staring at a silent spinner.
 *
 * Design: matches the "Automatic Fail Banner" style in EvaluationResults.tsx
 * (amber palette instead of red, ExclamationTriangleIcon).
 */
function SlowOperationBanner({
  active,
  thresholdSeconds = 90,
  reportUrl,
  className,
}: SlowOperationBannerProps) {
  const [show, setShow] = useState(false);

  // Reset visibility whenever the operation stops being active.
  useEffect(() => {
    if (!active) {
      setShow(false);
      return;
    }
    const id = setTimeout(() => setShow(true), thresholdSeconds * 1_000);
    return () => clearTimeout(id);
  }, [active, thresholdSeconds]);

  if (!show) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm',
        className
      )}
    >
      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-warning mt-0.5" aria-hidden="true" />
      <div className="flex-1 space-y-1">
        <p className="font-medium text-text-primary">This is taking longer than usual.</p>
        <p className="text-text-secondary">
          The system may be processing a backlog. Your result will appear when ready — you can
          safely leave this page and return.
        </p>
        {reportUrl && (
          <a
            href={reportUrl}
            className="inline-block text-accent underline underline-offset-2 hover:text-accent/80 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Report an issue
          </a>
        )}
      </div>
    </div>
  );
}

export default SlowOperationBanner;
